import { Dashboard } from "api/model/dashboard/Dashboard";
import { ContextConfig, ContextTypes, DateLoadConfig } from "api/model/dashboard/DashboardTab";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { Button } from "primereact/button";
import { T } from "services/lang/LanguageService";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { TIME_UNITS_WITHOUR_SECONDS } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { InputNumber } from "primereact/inputnumber";
import React, { useState } from "react";
import useDashboardStore from "store/dashboard-store";
import { ChronoUnitLegacy } from "utils/time-utils";
import { ChronoUnit } from "@js-joda/core";
import {
  defaultRangeDateConfig,
  defaultSingleDateSelectionConfig,
  defaultTabContextConfig,
  defaultYearsSelectionConfig,
} from "./dashboard-defaults";

interface DashboardTabConfiguratorProps {
  onHide: () => void;
  tabId: number;
  currentContextType?: ContextTypes;
  dashboard: Dashboard;
  setDashboard: (dashboard: Dashboard) => void;
}

const DashboardTabConfigurator = (props: DashboardTabConfiguratorProps) => {
  const setTabContextConfig = useDashboardStore(state => state.setTabContextConfig);
  const tabContextConfig = useDashboardStore(state => state.getTabContextConfig(props.tabId));
  const currentTab = props.dashboard?.dashboardTabsJs?.[props.tabId] ?? null;

  const [currentTabGroupId, setCurrentTabGroupId] = useState<string | null>(
    currentTab?.tabGroupId ?? null
  );

  const [newContextType, setNewContextType] = useState<ContextTypes>(
    props.currentContextType ?? ContextTypes.NONE
  );
  const [currentContextConfig, setCurrentContextConfig] = useState<ContextConfig>(
    tabContextConfig ?? defaultTabContextConfig
  );

  const resetContextConfig = (newType: ContextTypes) => {
    const newConfig: ContextConfig = {
      contextType: newType,
      showBorders: currentContextConfig.showBorders,
    };

    if (newType === ContextTypes.SINGLE_DATE_SELECTION_WITH_CONFIG) {
      newConfig.config = defaultSingleDateSelectionConfig;
    } else if (newType === ContextTypes.RANGE_DATE_SELECTION) {
      newConfig.config = defaultRangeDateConfig;
    } else if (newType === ContextTypes.YEARS_SELECTION) {
      newConfig.config = defaultYearsSelectionConfig;
    } else {
      newConfig.config = {};
    }

    setCurrentContextConfig(newConfig);
  };

  const handleContextTypeChange = (newType: ContextTypes) => {
    setNewContextType(newType);
    resetContextConfig(newType);
  };

  const handleChronoUnitChange = (field: keyof DateLoadConfig, value: ChronoUnit) => {
    const convertedValue = String(value).toUpperCase();
    setCurrentContextConfig(prevConfig => ({
      ...prevConfig,
      contextType: newContextType,
      config: {
        ...prevConfig.config,
        [field]: convertedValue,
      },
    }));
  };

  const handleNumberChange = (field: keyof DateLoadConfig, value: number | null) => {
    setCurrentContextConfig(prevConfig => ({
      ...prevConfig,
      contextType: newContextType,
      config: {
        ...prevConfig.config,
        [field]: value,
      },
    }));
  };

  const handleSaveTabConfigurator = () => {
    props.onHide();

    const updatedTabs = [...props.dashboard.dashboardTabsJs];

    updatedTabs[props.tabId] = {
      ...updatedTabs[props.tabId],
      contextConfig: currentContextConfig,
      ...(currentTabGroupId ? { tabGroupId: currentTabGroupId } : {}),
    };

    const newDashboard = {
      ...props.dashboard,
      dashboardTabsJs: updatedTabs,
    };

    setTabContextConfig(props.tabId, { ...currentContextConfig });
    props.setDashboard(newDashboard);
  };

  const handleTabConfiguratorFooter = () => {
    return (
      <div className="flex flex-row justify-content-between align-items-end">
        <div className="flex flex-column gap-2">
          <label htmlFor="showGroupsBoarders" className="text-left">
            {T("action.show-group-borders")}
          </label>
          <InputSwitch
            checked={Boolean(currentContextConfig?.showBorders)}
            onChange={e => {
              setCurrentContextConfig(prevConfig => ({
                ...prevConfig,
                contextType: newContextType,
                config: {
                  ...prevConfig.config,
                },
                showBorders: e.value,
              }));
            }}
          />
        </div>
        <div className="flex flex-row gap-2 h-3rem justify-content-end ml-auto">
          <Button
            label={T("word.cancel")}
            icon="pi pi-times"
            onClick={props.onHide}
            className="p-button-sm p-button-danger"
          />
          <Button
            label={T("action.save")}
            icon="pi pi-check"
            onClick={handleSaveTabConfigurator}
            className="p-button-sm p-button-success"
          />
        </div>
      </div>
    );
  };

  return (
    <Dialog
      header={T("label.tabs-configurator")}
      visible
      className="w-30rem"
      style={{ minHeight: "23rem" }}
      onHide={props.onHide}
      footer={handleTabConfiguratorFooter}
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-2">
          <label htmlFor="tabGroupName">{T("label.tab-group-name")}</label>
          <Dropdown
            id="tabGroupName"
            options={props.dashboard?.tabGroups}
            value={currentTabGroupId}
            optionLabel="title"
            optionValue="uuid"
            onChange={e => setCurrentTabGroupId(e.value)}
            placeholder={
              props.dashboard?.tabGroups?.length
                ? undefined
                : T("message.first-add-groups-to-current-dashboard")
            }
            disabled={props.dashboard.tabGroups?.length === 0}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="referenceTimeType">{T("label.context-type")}</label>
          <Dropdown
            id="referenceTimeType"
            options={Object.values(ContextTypes)}
            value={newContextType}
            onChange={e => handleContextTypeChange(e.value)}
          />
        </div>
        {newContextType !== ContextTypes.NONE &&
          newContextType !== ContextTypes.SINGLE_DATE_SELECTION &&
          newContextType !== ContextTypes.YEARS_SELECTION && (
            <div className="flex flex-column gap-2">
              <div className="flex flex-column gap-2">
                <label htmlFor="alignStartTo">{T("load-conf.align-start-to")}</label>
                <TimeUnitDropdown
                  id="alignStartTo"
                  className="p-0"
                  options={TIME_UNITS_WITHOUR_SECONDS}
                  value={currentContextConfig.config?.alignStartTo ?? ChronoUnitLegacy.HOURS}
                  onChange={e => {
                    handleChronoUnitChange("alignStartTo", e.value);
                  }}
                />
              </div>
              <label htmlFor="timeOffset">{T("load-conf.time-offset")}</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="timeOffset"
                  value={(currentContextConfig.config?.timeOffset as number) ?? 0}
                  onValueChange={e => {
                    handleNumberChange("timeOffset", e.value ?? 0);
                  }}
                />
                <TimeUnitDropdown
                  id="timeOffsetUnit"
                  className="p-inputgroup-addon p-0"
                  options={TIME_UNITS_WITHOUR_SECONDS}
                  value={currentContextConfig.config?.timeOffsetUnit ?? ChronoUnitLegacy.HOURS}
                  onChange={e => {
                    handleChronoUnitChange("timeOffsetUnit", e.value);
                  }}
                />
              </div>
            </div>
          )}
        {newContextType === ContextTypes.RANGE_DATE_SELECTION && (
          <div className="flex flex-column gap-3">
            <div className="flex flex-column gap-2">
              <label htmlFor="lag">{T("load-conf.lag")}</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lag"
                  value={(currentContextConfig.config?.lag as number) ?? 0}
                  onValueChange={e => {
                    handleNumberChange("lag", e.value ?? 0);
                  }}
                />
                <TimeUnitDropdown
                  id="lagUnit"
                  className="p-inputgroup-addon p-0"
                  options={TIME_UNITS_WITHOUR_SECONDS}
                  value={currentContextConfig.config?.lagUnit ?? ChronoUnitLegacy.HOURS}
                  onChange={e => {
                    handleChronoUnitChange("lagUnit", e.value);
                  }}
                />
              </div>
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="lead">{T("load-conf.lead")}</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lead"
                  value={(currentContextConfig.config?.lead as number) ?? 0}
                  onValueChange={e => {
                    handleNumberChange("lead", e.value ?? 0);
                  }}
                />
                <TimeUnitDropdown
                  id="leadUnit"
                  className="p-inputgroup-addon p-0"
                  options={TIME_UNITS_WITHOUR_SECONDS}
                  value={currentContextConfig.config?.leadUnit ?? ChronoUnitLegacy.HOURS}
                  onChange={e => {
                    handleChronoUnitChange("leadUnit", e.value);
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};
export default DashboardTabConfigurator;
